import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import axios from 'axios';
import { Button } from './components/ui/button';
import './App.css';

// Page imports
import HomePage from './pages/Home';
import OnboardingWizard from './pages/OnboardingWizard';
import StrategyAssessment from './pages/StrategyAssessment';
import StrategyRecommendations from './pages/StrategyRecommendations';
import EBITDABusinessCase from './pages/EBITDABusinessCase';
import QRMSuccessCases from './pages/QRMSuccessCases';
import QRMFinancialLab from './pages/QRMFinancialLab';
import QRMSoftwarePartners from './pages/QRMSoftwarePartners';
import QRMJourney from './pages/QRMJourney';
import QRMEducation from './pages/QRMEducation';
import StartJourneyPage from './pages/StartJourneyPage';
import StrategicConsulting from './pages/StrategicConsulting';
import BANITUNAPage from './pages/BANITUNAPage';
import CutTheFogPage from './pages/CutTheFogPage';
import QRMVocabulary from './pages/QRMVocabulary';
import ImplementationCosts from './pages/ImplementationCosts';
import ROISimulator from './pages/ROISimulator';
import ImplementationPlanning from './pages/ImplementationPlanning';
import PricingPage from './pages/PricingPage';
import PaymentSuccess from './pages/PaymentSuccess';
import AskJanAI from './pages/AskJanAI';
import ContactPage from './pages/ContactPage';
import AdminDashboard from './pages/AdminDashboard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PageViewTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    const trackPageView = async () => {
      try {
        await axios.post(`${API}/track-pageview`, {
          page: location.pathname,
          referrer: document.referrer || 'direct',
          user_agent: navigator.userAgent
        });
      } catch (error) {
        console.log('Page view tracking skipped');
      }
    };
    
    trackPageView();
  }, [location.pathname]);
  
  return null;
};

function AppContent() {
  return (
    <div className="App">
      {/* Fixed Site-Wide Header */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20 sm:h-24">
            <a href="/" className="flex items-center gap-3 sm:gap-4 shrink-0" data-testid="header-logo">
              {/* Standalone N-compass icon — full header height */}
              <img 
                src="/logo-icon.png" 
                alt="" 
                className="h-12 sm:h-16 w-auto shrink-0"
                style={{ mixBlendMode: 'multiply' }}
              />
              {/* Brand text */}
              <div className="flex flex-col" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <span className="text-lg sm:text-2xl font-bold tracking-tight leading-tight" style={{ color: '#0F2E58' }}>
                  SMART QRM <span className="font-medium" style={{ color: '#6B7280' }}>NAVIGATOR</span>
                </span>
                <span className="text-[10px] sm:text-xs tracking-[0.16em] font-medium leading-tight mt-0.5" style={{ color: '#9B9B9B' }}>QUICK RESPONSIVE MANUFACTURING</span>
              </div>
            </a>
            <nav className="hidden md:flex items-center gap-1">
              <a href="/business-case" className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2 transition-colors" data-testid="nav-solutions">Solutions</a>
              <a href="/qrm-education" className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2 transition-colors" data-testid="nav-methodology">Methodology</a>
              <a href="/success-cases" className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2 transition-colors" data-testid="nav-success">Success Stories</a>
              <a href="/qrm-journey" className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2 transition-colors" data-testid="nav-about">About Jan</a>
              <a href="/contact" className="text-sm text-gray-600 hover:text-gray-900 px-3 py-2 transition-colors" data-testid="nav-contact">Contact</a>
              <a href="/business-case" className="ml-3 text-sm font-bold px-5 py-2.5 transition-all duration-200 rounded shadow-sm hover:shadow-md" style={{ backgroundColor: '#EAB308', color: '#0A192F' }} data-testid="nav-cta">Calculate ROI</a>
            </nav>
            <button className="md:hidden text-gray-700" onClick={() => { const m = document.getElementById('mobile-nav'); m.classList.toggle('hidden'); }} data-testid="nav-mobile-toggle">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
            </button>
          </div>
        </div>
        {/* Mobile Menu */}
        <div id="mobile-nav" className="hidden md:hidden border-t border-gray-100 pb-4 px-4 bg-white">
          <a href="/business-case" className="block text-sm text-gray-700 hover:text-gray-900 py-3 border-b border-gray-50">Solutions</a>
          <a href="/qrm-education" className="block text-sm text-gray-700 hover:text-gray-900 py-3 border-b border-gray-50">Methodology</a>
          <a href="/success-cases" className="block text-sm text-gray-700 hover:text-gray-900 py-3 border-b border-gray-50">Success Stories</a>
          <a href="/qrm-journey" className="block text-sm text-gray-700 hover:text-gray-900 py-3 border-b border-gray-50">About Jan</a>
          <a href="/contact" className="block text-sm text-gray-700 hover:text-gray-900 py-3 border-b border-gray-50">Contact</a>
          <a href="/qrm-vocabulary" className="block text-sm text-gray-700 hover:text-gray-900 py-3 border-b border-gray-50">Vocabulary</a>
          <a href="/software-partners" className="block text-sm text-gray-700 hover:text-gray-900 py-3 border-b border-gray-50">Partners</a>
          <a href="/bani-tuna" className="block text-sm text-gray-700 hover:text-gray-900 py-3 border-b border-gray-50">BANI/TUNA</a>
          <a href="/implementation-costs" className="block text-sm text-gray-700 hover:text-gray-900 py-3 border-b border-gray-50">Costs</a>
          <a href="/financial-lab" className="block text-sm text-gray-700 hover:text-gray-900 py-3 border-b border-gray-50">Financial Lab</a>
          <a href="/ask-jan" className="block text-sm text-gray-700 hover:text-gray-900 py-3 border-b border-gray-50">Ask Jan AI</a>
          <a href="/business-case" className="block text-sm font-bold py-3 px-4 mt-2 text-center rounded" style={{ backgroundColor: '#EAB308', color: '#0A192F' }}>Calculate ROI</a>
        </div>
      </div>
      
      {/* Main Content with Top Padding */}
      <div className="pt-20 sm:pt-24">
        <BrowserRouter>
          <PageViewTracker />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/navigator" element={<OnboardingWizard />} />
            <Route path="/assessment" element={<StrategyAssessment />} />
            <Route path="/recommendations" element={<StrategyRecommendations />} />
            <Route path="/business-case" element={<EBITDABusinessCase />} />
            <Route path="/success-cases" element={<QRMSuccessCases />} />
            <Route path="/financial-lab" element={<QRMFinancialLab />} />
            <Route path="/software-partners" element={<QRMSoftwarePartners />} />
            <Route path="/qrm-journey" element={<QRMJourney />} />
            <Route path="/qrm-education" element={<QRMEducation />} />
            <Route path="/start-journey" element={<StartJourneyPage />} />
            <Route path="/strategic-consulting" element={<StrategicConsulting />} />
            <Route path="/bani-tuna" element={<BANITUNAPage />} />
            <Route path="/cut-the-fog" element={<CutTheFogPage />} />
            <Route path="/qrm-vocabulary" element={<QRMVocabulary />} />
            <Route path="/implementation-costs" element={<ImplementationCosts />} />
            <Route path="/roi-simulator" element={<ROISimulator />} />
            <Route path="/implementation-plan" element={<ImplementationPlanning />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/ask-jan" element={<AskJanAI />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="*" element={<HomePage />} />
          </Routes>
          
          {/* STICKY FLOATING CTA BUTTON - BOTTOM RIGHT */}
          <div className="fixed bottom-6 right-6 z-40">
            <Button 
              className="px-6 py-3 font-bold shadow-lg transition-all duration-300 transform hover:scale-105 rounded hover:opacity-90"
              style={{ backgroundColor: '#EAB308', color: '#0A192F' }}
              onClick={() => window.location.href = '/business-case'}
              data-testid="floating-cta"
            >
              Calculate ROI
            </Button>
          </div>
          
          {/* BOTTOM FOOTER */}
          <footer className="py-8 border-t border-gray-200 bg-gray-50">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4 sm:gap-6 flex-wrap justify-center">
                  <a href="/" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Home</a>
                  <a href="/qrm-vocabulary" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Vocabulary</a>
                  <a href="/software-partners" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Partners</a>
                  <a href="/bani-tuna" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">BANI/TUNA</a>
                  <a href="/implementation-costs" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Costs</a>
                  <a href="/recommendations" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Strategy Advisor</a>
                  <a href="/strategic-consulting" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Consulting</a>
                  <a href="/financial-lab" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Financial Lab</a>
                  <a href="/ask-jan" className="text-sm text-gray-500 hover:text-gray-900 transition-colors">Ask Jan AI</a>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-xs text-gray-400">info@smartqrmnavigator.com</span>
                  <a href="/admin" className="text-xs text-gray-500 hover:text-gray-900 font-medium transition-colors px-2 py-1 border border-gray-200 rounded" data-testid="footer-admin-link">Admin Dashboard</a>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-gray-200 text-center">
                <p className="text-xs text-gray-400">&copy; {new Date().getFullYear()} Smart QRM Navigator — JP Consulting. All rights reserved.</p>
              </div>
            </div>
          </footer>
        </BrowserRouter>
      </div>
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AppContent />
    </HelmetProvider>
  );
}

export default App;
